/**
 * Parse a single line of `adb logcat -v threadtime` output.
 *
 * Format reference:
 *   MM-DD HH:MM:SS.mmm  PID  TID L tag    : message
 * Example:
 *   11-23 17:30:00.123  1234  5678 D MyTag  : hello world
 *
 * Notes:
 *  - Tag may contain spaces & special chars; we capture everything up to the
 *    last `: ` before the message. We use a permissive regex and fall back to
 *    `unknown` fields when parsing fails so the raw line is still rendered.
 *  - Level is one of V/D/I/W/E/F/A. Anything else is mapped to '?'.
 */
export type LogLevel = 'V' | 'D' | 'I' | 'W' | 'E' | 'F' | 'A' | '?';

export interface LogLine {
  /** Original raw text (no trailing newline). */
  raw: string;
  ts: string;
  pid: string;
  tid: string;
  level: LogLevel;
  tag: string;
  message: string;
  /**
   * 单调递增的全局序号；由 useLogcatStream.appendChunk 在入队时按
   * `totalSeen - 1` 写入。Phase 14 日志检查点引擎把它作为 RunnerSet
   * 的 lineId，让行内 marker 可以反查命中。
   */
  id?: number;
}

const THREADTIME_RE =
  /^(\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}\.\d{1,6})\s+(\d+)\s+(\d+)\s+([VDIWEFA])\s+([^:]+?)\s*:\s?(.*)$/;

const LEVELS = new Set(['V', 'D', 'I', 'W', 'E', 'F', 'A']);

export function parseLogLine(line: string): LogLine {
  const raw = line.replace(/\r$/, '');
  const m = THREADTIME_RE.exec(raw);
  if (m) {
    const lv = m[4];
    return {
      raw,
      ts: m[1],
      pid: m[2],
      tid: m[3],
      level: (LEVELS.has(lv) ? lv : '?') as LogLevel,
      tag: m[5].trim(),
      message: m[6],
    };
  }
  return {
    raw,
    ts: '',
    pid: '',
    tid: '',
    level: '?',
    tag: '',
    message: raw,
  };
}

export const LEVEL_ORDER: Record<LogLevel, number> = {
  V: 0,
  D: 1,
  I: 2,
  W: 3,
  E: 4,
  F: 5,
  A: 6,
  '?': -1,
};

export const LEVEL_LABEL: Record<LogLevel, string> = {
  V: 'Verbose',
  D: 'Debug',
  I: 'Info',
  W: 'Warn',
  E: 'Error',
  F: 'Fatal',
  A: 'Assert',
  '?': '其它',
};

/**
 * Tailwind helper for level → text colour. Used by the log list rows.
 */
export function levelClass(level: LogLevel): string {
  switch (level) {
    case 'E':
    case 'F':
    case 'A':
      return 'text-rose-500';
    case 'W':
      return 'text-amber-500';
    case 'I':
      return 'text-emerald-400';
    case 'D':
      return 'text-sky-400';
    case 'V':
      return 'text-ink-muted';
    default:
      return 'text-ink-muted';
  }
}
